import { Perf } from 'r3f-perf';
import { useRef } from 'react';
import { type Group } from 'three';
import { XROrigin } from '@react-three/xr';
import { Background } from './Background';
import { Effects } from './Effects';
import { Navigator } from './Navigator';
import { Hub, HubBrand } from '@/objects/Hub';
import { Highways } from '@/objects/Highways';
import { PageScreen } from '@/objects/PageScreen';
import { VRReturnButton } from '@/objects/VRReturnButton';
import { WarpTunnel } from '@/objects/WarpTunnel';
import { useDebugStore } from '@/store/useDebugStore';
import { palette } from '@/theme/palette';

/**
 * Pinorion — galactic crossroads landing.
 *
 * The crossroads (Hub + Highways + per-page destination screens)
 * lives inside the rotating `worldRef` group: when the user clicks a
 * highway, Navigator yaws this group so the selected corridor aligns
 * with -Z, then warps the camera forward through the WarpTunnel to
 * the destination PageScreen.
 */
export function Scene() {
  const showPerf = useDebugStore((s) => s.showPerf);
  const worldRef = useRef<Group>(null);
  const xrOriginRef = useRef<Group>(null);

  return (
    <>
      <color attach="background" args={[palette.bg]} />
      <Background />

      <group ref={worldRef}>
        <Hub />
        <Highways />
        <PageScreen />
      </group>

      {/* Brand text sits outside the rotating world so it stays flat
          and readable while the kavşak spins. */}
      <HubBrand />

      {/* XR origin — the user's reference frame inside an immersive
          session. Navigator moves this group to "snap-teleport" the
          user when a page is selected. The Geri Dön button lives
          inside it so it follows the user's chest height. */}
      <XROrigin ref={xrOriginRef}>
        <VRReturnButton />
      </XROrigin>

      <WarpTunnel />
      <Navigator worldRef={worldRef} xrOriginRef={xrOriginRef} />

      <Effects />
      {showPerf && <Perf position="top-left" />}
    </>
  );
}
